import React, { useEffect, useState } from "react";

import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";

import request from "../../../Helpers/API";

// El listado (tipo_contacto) usa el texto completo en mayúsculas (ej. "SERVICIO"), pero la columna
// de guardado en base de datos es de 2 caracteres — se traduce a código corto solo al guardar
const TIPOS_CONTACTO = [
  { id: "COBRANZA", codigo: "CO", display: "Cobranza" },
  { id: "CREDITO", codigo: "CR", display: "Crédito" },
  { id: "VENTAS", codigo: "VE", display: "Ventas" },
  { id: "REFACCIONES", codigo: "RE", display: "Refacciones" },
  { id: "SERVICIO", codigo: "SE", display: "Servicio" },
];

// Cada medio es ahora una lista de valores — {valor, responsable, orden} — para permitir varios
// correos/teléfonos/whatsapp del mismo tipo de contacto, cada uno con su propio responsable
// (orden los diferencia en el backend). El comentario es único por tipo de contacto.
const FORM_VACIO = { tipo_contacto: "", comentarios: "", correo: [], telefono: [], whatsapp: [] };

// Solo dígitos, máximo 10 (mismo criterio de captura de Teléfono usado en Datos generales)
const filtrarTelefono = (value) => value.replace(/\D/g, "").slice(0, 10);

// Caracteres válidos en un correo (local-part + dominio), máximo 100
const filtrarCorreo = (value) => value.replace(/[^a-zA-Z0-9@._\-+]/g, "").slice(0, 100);

// Estructura mínima de correo: algo@algo.algo
const REGEX_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Un registro {valor, responsable, orden} por elemento de la lista de un medio — si no hay
// ninguno se arranca con un input vacío para que siempre haya al menos una fila que capturar
const mapMedioToLista = (registros = []) =>
  registros.length > 0
    ? registros.map((r) => ({ valor: r.valor || "", responsable: r.responsable || "", orden: r.orden }))
    : [{ valor: "", responsable: "", orden: null }];

// Reconstruye el formulario a partir de las filas ya cargadas en memoria (una por medio) para
// el tipo de contacto dado — evita otro request, el padre ya tiene el listado completo
const mapFilaToForm = (fila) => ({
  tipo_contacto: fila?.tipo_contacto || "",
  comentarios: fila?.comentarios || "",
  correo: mapMedioToLista(fila?.CORREO),
  telefono: mapMedioToLista(fila?.TELEFONO),
  whatsapp: mapMedioToLista(fila?.WHATSAPP),
});

export const CRM_ModalContacto = ({ open, onClose, idcliente, fila, filasExistentes = [], onGuardado }) => {
  const esEdicion = !!fila;
  const [form, setForm] = useState(FORM_VACIO);

  useEffect(() => {
    if (!open) return;
    setForm(esEdicion ? mapFilaToForm(fila) : FORM_VACIO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fila]);

  // Si en modo alta se elige un tipo que ya tiene contactos capturados, se resuelve como
  // edición de esa fila (mismo orden por medio) para no duplicar registros en el backend
  const filaActiva = esEdicion ? fila : filasExistentes.find((f) => f.tipo_contacto === form.tipo_contacto) || null;

  const handleChange = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  // Actualiza el valor de un elemento puntual de la lista de un medio (correo/telefono/whatsapp)
  const handleChangeMedio = (campo, index, filtro) => (e) => {
    const valor = filtro ? filtro(e.target.value) : e.target.value;
    setForm((prev) => ({
      ...prev,
      [campo]: prev[campo].map((item, i) => (i === index ? { ...item, valor } : item)),
    }));
  };

  // Actualiza el responsable de un elemento puntual de la lista de un medio — cada correo/teléfono/whatsapp tiene el suyo
  const handleChangeResponsableMedio = (campo, index) => (e) => {
    const responsable = e.target.value;
    setForm((prev) => ({
      ...prev,
      [campo]: prev[campo].map((item, i) => (i === index ? { ...item, responsable } : item)),
    }));
  };

  const handleAgregarMedio = (campo) => {
    setForm((prev) => ({ ...prev, [campo]: [...prev[campo], { valor: "", responsable: "", orden: null }] }));
  };

  const handleQuitarMedio = (campo, index) => {
    setForm((prev) => {
      const lista = prev[campo].filter((_, i) => i !== index);
      return { ...prev, [campo]: lista.length > 0 ? lista : [{ valor: "", responsable: "", orden: null }] };
    });
  };

  const handleChangeTipo = (e) => {
    const tipo_contacto = e.target.value;
    const existente = filasExistentes.find((f) => f.tipo_contacto === tipo_contacto);
    setForm(existente ? { ...mapFilaToForm(existente), tipo_contacto } : { ...FORM_VACIO, tipo_contacto });
  };

  const validar = () => {
    if (!form.tipo_contacto) { Swal.fire("CONTACTOS", "El tipo de contacto es obligatorio", "warning"); return false; }
    const hayAlgunValor = [...form.correo, ...form.telefono, ...form.whatsapp].some((m) => m.valor.trim());
    if (!hayAlgunValor) {
      Swal.fire("CONTACTOS", "Debes ingresar al menos un medio de contacto", "warning");
      return false;
    }
    const correoInvalido = form.correo.some((m) => m.valor.trim() && !REGEX_CORREO.test(m.valor.trim()));
    if (correoInvalido) {
      Swal.fire("CONTACTOS", "Uno de los correos capturados no tiene un formato válido", "warning");
      return false;
    }
    return true;
  };

  const handleGuardar = () => {
    if (!validar()) return;

    // El backend espera el código corto (2 caracteres) en tipo_contacto, no el texto completo
    const codigoTipo = TIPOS_CONTACTO.find((t) => t.id === form.tipo_contacto)?.codigo || form.tipo_contacto;

    // Cada medio puede tener varios valores — se manda un registro por cada uno. El orden es
    // único por CLIENTE (no por medio/tipo), así que los registros nuevos mandan orden:null y
    // es el propio stored quien calcula el siguiente consecutivo — evita que dos medios distintos
    // (ej. un correo nuevo y un teléfono nuevo) calculen el mismo orden y choquen contra la PK.
    // Los registros que ya existían (traen orden) pero se vaciaron/quitaron de la lista se
    // mandan con estatus:false para darlos de baja en vez de simplemente omitirlos
    const construirMedio = (letra, lista, registrosOriginales = []) => {
      const vigentes = lista
        .filter((m) => m.valor.trim())
        .map((m) => ({
          idcliente: Number(idcliente),
          orden: m.orden != null ? Number(m.orden) : null,
          medio_contacto: letra,
          tipo_contacto: codigoTipo,
          valor: m.valor.trim(),
          comentarios: form.comentarios.trim(),
          responsable: m.responsable.trim(),
          estatus: true,
        }));

      const ordenesVigentes = vigentes.map((v) => v.orden);
      const bajas = registrosOriginales
        .filter((r) => r.orden != null && !ordenesVigentes.includes(Number(r.orden)))
        .map((r) => ({
          idcliente: Number(idcliente),
          orden: Number(r.orden),
          medio_contacto: letra,
          tipo_contacto: codigoTipo,
          valor: r.valor || "",
          comentarios: form.comentarios.trim(),
          responsable: r.responsable || "",
          estatus: false,
        }));

      return [...vigentes, ...bajas];
    };

    const contactos = [
      ...construirMedio("C", form.correo, filaActiva?.CORREO),
      ...construirMedio("T", form.telefono, filaActiva?.TELEFONO),
      ...construirMedio("W", form.whatsapp, filaActiva?.WHATSAPP),
    ];

    request
      .post("ClientesDatosContacto/GuardarArray", { contactos: JSON.stringify({ contactosCliente: contactos }) })
      .then(async (result) => {
        await Swal.fire("CONTACTOS", result?.mensaje || "Contacto guardado con éxito", "success");
        onGuardado(result?.listado);
        onClose();
      })
      .catch((error) => Swal.fire("CONTACTOS", JSON.stringify(error), "error"));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="crm-modal-card">
        <button type="button" className="crm-modal-cerrar" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </button>

        <h3 className="crm-modal-titulo">{esEdicion ? "Editar contacto" : "Agregar contacto"}</h3>

        <div className="crm-modal-body">
          {!esEdicion && (
            <div className="crm-field">
              <label>Tipo de contacto</label>
              <select className="crm-field-editable" value={form.tipo_contacto} onChange={handleChangeTipo}>
                <option value="">Seleccionar</option>
                {TIPOS_CONTACTO.map((o) => <option key={o.id} value={o.id}>{o.display}</option>)}
              </select>
            </div>
          )}

          <div className="crm-field">
            <label>Comentario</label>
            <textarea className="crm-field-editable" rows={2} maxLength={500} value={form.comentarios} onChange={handleChange("comentarios")} />
          </div>

          <div className="crm-field">
            <label>Correo</label>
            {form.correo.map((m, i) => (
              <div className="crm-field-lista-item crm-field-lista-item-medio" key={i}>
                <div className="crm-field-lista-item-inputs">
                  <input
                    type="text"
                    className="crm-field-editable"
                    maxLength={100}
                    placeholder="Correo"
                    value={m.valor}
                    onChange={handleChangeMedio("correo", i, filtrarCorreo)}
                  />
                  <input
                    type="text"
                    className="crm-field-editable"
                    maxLength={200}
                    placeholder="Responsable"
                    value={m.responsable}
                    onChange={handleChangeResponsableMedio("correo", i)}
                  />
                </div>
                {form.correo.length > 1 && (
                  <button type="button" className="btn-lista-quitar" title="Quitar" onClick={() => handleQuitarMedio("correo", i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                )}
                {m.valor.trim() && !REGEX_CORREO.test(m.valor.trim()) && (
                  <span className="crm-field-advertencia">El correo no tiene un formato válido</span>
                )}
              </div>
            ))}
            <button type="button" className="btn-lista-agregar" onClick={() => handleAgregarMedio("correo")}>
              <AddCircleOutlineIcon fontSize="small" /> Agregar correo
            </button>
          </div>

          <div className="crm-field">
            <label>Llamadas</label>
            {form.telefono.map((m, i) => (
              <div className="crm-field-lista-item crm-field-lista-item-medio" key={i}>
                <div className="crm-field-lista-item-inputs">
                  <input
                    type="text"
                    className="mono crm-field-editable"
                    maxLength={10}
                    placeholder="Teléfono"
                    value={m.valor}
                    onChange={handleChangeMedio("telefono", i, filtrarTelefono)}
                  />
                  <input
                    type="text"
                    className="crm-field-editable"
                    maxLength={200}
                    placeholder="Responsable"
                    value={m.responsable}
                    onChange={handleChangeResponsableMedio("telefono", i)}
                  />
                </div>
                {form.telefono.length > 1 && (
                  <button type="button" className="btn-lista-quitar" title="Quitar" onClick={() => handleQuitarMedio("telefono", i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                )}
                {m.valor && m.valor.length !== 10 && (
                  <span className="crm-field-advertencia">El teléfono debe tener 10 dígitos</span>
                )}
              </div>
            ))}
            <button type="button" className="btn-lista-agregar" onClick={() => handleAgregarMedio("telefono")}>
              <AddCircleOutlineIcon fontSize="small" /> Agregar teléfono
            </button>
          </div>

          <div className="crm-field">
            <label>WhatsApp</label>
            {form.whatsapp.map((m, i) => (
              <div className="crm-field-lista-item crm-field-lista-item-medio" key={i}>
                <div className="crm-field-lista-item-inputs">
                  <input
                    type="text"
                    className="mono crm-field-editable"
                    maxLength={10}
                    placeholder="WhatsApp"
                    value={m.valor}
                    onChange={handleChangeMedio("whatsapp", i, filtrarTelefono)}
                  />
                  <input
                    type="text"
                    className="crm-field-editable"
                    maxLength={200}
                    placeholder="Responsable"
                    value={m.responsable}
                    onChange={handleChangeResponsableMedio("whatsapp", i)}
                  />
                </div>
                {form.whatsapp.length > 1 && (
                  <button type="button" className="btn-lista-quitar" title="Quitar" onClick={() => handleQuitarMedio("whatsapp", i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                )}
                {m.valor && m.valor.length !== 10 && (
                  <span className="crm-field-advertencia">El WhatsApp debe tener 10 dígitos</span>
                )}
              </div>
            ))}
            <button type="button" className="btn-lista-agregar" onClick={() => handleAgregarMedio("whatsapp")}>
              <AddCircleOutlineIcon fontSize="small" /> Agregar WhatsApp
            </button>
          </div>
        </div>

        <div className="crm-modal-buttons">
          <button type="button" className="btn-secundario-outline" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn-primario-solido" onClick={handleGuardar}>Guardar</button>
        </div>
      </div>
    </Modal>
  );
};
